import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { HistoryPanel } from '../history/HistoryPanel';
import { ConflictNotice } from '../ui/ConflictNotice';
import { KitEditor, KITS_KEY } from './KitEditor';
import { SectionNav } from '../templates/SectionNav';
import { DialogLayer } from '../ui/Dialog';
import { leaveGuarded } from '../ui/unsaved';

const RESOURCE = 'kits.kits';
type KitSummary = { id: string; name: string; notes: string; lineCount: number; version: number };
type ListResponse = { kits: KitSummary[]; truncated: boolean };

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function DeleteDialog({ kit, onClose }: { kit: KitSummary; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(`/api/kits/${kit.id}?version=${kit.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: KITS_KEY });
      onClose();
    },
  });
  const error = remove.error instanceof ApiError ? remove.error : null;
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="delete-title">
        <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="delete-title">Usunąć zestaw?</h2>
        <p>„{kit.name}” zniknie z listy. Zestaw zostaje w bazie, a w historii zmian zostaje wpis o usunięciu.</p>
        {error?.status === 409 && (
          <ConflictNotice
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: KITS_KEY });
              onClose();
            }}
          />
        )}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Usuwanie…' : 'Usuń zestaw'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

export function KitsPage() {
  const { me } = useMe();
  const [search, setSearch] = useState('');
  // `undefined`: editor closed; `null`: adding a kit; an id: editing that kit.
  const [editing, setEditing] = useState<string | null | undefined>(undefined);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<KitSummary | null>(null);
  const q = useDebounced(search.trim(), 250);

  const list = useQuery({
    queryKey: [...KITS_KEY, 'list', q],
    placeholderData: keepPreviousData,
    queryFn: () => api<ListResponse>(`/api/kits${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  });
  if (!me) return null;
  const kits = list.data?.kits ?? [];

  return (
    <section className="page" data-feature-id="kits.list">
      <div className="page-head">
        <h1>Szablony</h1>
        {can(me, RESOURCE, 'create') && editing === undefined && (
          <button className="btn primary" type="button" data-feature-id="kits.edit" onClick={() => leaveGuarded(() => setEditing(null))}>
            Dodaj zestaw
          </button>
        )}
      </div>
      <SectionNav me={me} />
      <p className="muted">Zestaw to kilka pozycji sprzętu, które zwykle jadą razem, np. „Mikrofon wokalowy ze statywem i kablem”. Wybierzesz go w sprzętówce, w szablonie i w kosztorysie.</p>

      {editing !== undefined && <KitEditor key={editing ?? 'new'} {...(editing !== null ? { kitId: editing } : {})} onClose={() => setEditing(undefined)} />}

      <div className="filters">
        <label className="field">
          <span>Szukaj</span>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nazwa zestawu" autoComplete="off" />
        </label>
      </div>

      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && (
        <p className="alert" role="alert">
          {list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać zestawów'}
        </p>
      )}
      {list.data && kits.length === 0 && <p className="muted">Nie znaleziono zestawów.</p>}
      {list.data?.truncated && <p className="notice">Pokazano pierwszych 200 zestawów. Zawęź wyszukiwanie, żeby zobaczyć pozostałe.</p>}

      <ul className="list">
        {kits.map((k) => (
          <li key={k.id} className="row">
            <div className="row-main">
              <div className="row-title">
                <strong>{k.name}</strong>
                <span className="chip">Pozycji: {k.lineCount}</span>
              </div>
              {k.notes && <div className="muted">{k.notes}</div>}
            </div>
            <div className="actions row-actions">
              <button className="btn small" type="button" data-feature-id="kits.preview" aria-label={`Skład: ${k.name}`} onClick={() => leaveGuarded(() => setEditing(k.id))}>
                {can(me, RESOURCE, 'edit') ? 'Edytuj / skład' : 'Skład'}
              </button>
              {can(me, 'history.entries', 'view') && (
                <button className="btn small" type="button" aria-expanded={historyId === k.id} onClick={() => setHistoryId(historyId === k.id ? null : k.id)}>
                  Historia zmian
                </button>
              )}
              {can(me, RESOURCE, 'delete') && (
                <button className="btn small" type="button" aria-label={`Usuń: ${k.name}`} onClick={() => setDeleting(k)}>
                  Usuń
                </button>
              )}
            </div>
            {historyId === k.id && can(me, 'history.entries', 'view') && <HistoryPanel entityId={k.id} resource={RESOURCE} />}
          </li>
        ))}
      </ul>

      {deleting && <DeleteDialog kit={deleting} onClose={() => setDeleting(null)} />}
    </section>
  );
}
